import { isArray, isNull, isUndefined } from 'lodash';
import {
  MessageType,
  interactiveSchema,
  noIntentTemplateSchema,
  NoIntentTemplate,
  messageTemplateSchema,
  MsgTemplate,
  TemplateKeys,
  getTextTemplate,
  getInteractiveTemplateWithButtons,
  getUnknownTemplate,
  getNoIntentTemplate,
  getValidTemplate,
  getButtonsFromTemplate,
  getMediaTemplateFromObject,
  TemplateButtonTypeEnum,
  MessageEnvelope,
  TemplateButtons,
  getContactTemplate,
  contactTemplateSchema,
  getCarouselCardButtons,
} from '@zonofi/common';
import { isEmpty } from 'lodash';
import { TemplateTypeButtonsMap } from '../constants';
import {
  RetriveTemplateResult,
  TemplateButtonKeys,
  TemplateButtonsMapType,
  TemplateProcessResult,
} from '../types';
import {
  getCampaignStatus,
  getMessageActionsData,
  getTemplateKey,
  handleDataBasedTemplates,
} from '../functions';
import {
  TemplateButtonsMap,
  updateTemplateTypeButtonsMap,
} from '../constants/template-buttons';
import { useMemo } from 'react';
import { createButtonPropsMap } from '../functions/template-buttons-mapper';
import {
  getChannelById,
  getSelectedMessage,
  getSelectedPrincipalAccount,
  getUserPermissions,
  getWorkspace,
  store,
  useAppSelector,
  useGetLoggedInUserType,
} from '@zonofi/seller-rtk-store';
import { extractMessageProperties } from '../functions/message-extractor';

const determineMessageType = (
  formId: string | undefined,
  msgData: any,
  type: string
): string => {
  if (formId) return 'survey-form';

  const hasMsgData = !isUndefined(msgData) && !isNull(msgData);
  if (!hasMsgData) return type?.toLowerCase();

  if (
    msgData.isModified &&
    msgData.status?.toLowerCase()?.includes('submitted')
  ) {
    return 'Modified';
  }

  if (msgData.status === 'sendPaymentRequest') {
    return isArray(msgData.invoice)
      ? 'requestWithInvoice'
      : 'requestWithoutInvoice';
  }

  if (
    msgData.status &&
    [MessageType.PAYMENT, MessageType.ORDER].includes(type as MessageType)
  ) {
    return msgData.status?.toLowerCase();
  }

  return type?.toLowerCase();
};

const processTemplate = (
  originalTemplate: any,
  templateName: TemplateKeys | undefined,
  type: string,
  text: string
): TemplateProcessResult => {
  if (type === MessageType.CHANNEL_UPDATE) {
    return { template: getTextTemplate(text), isProcessed: true };
  }

  if (type === MessageType.INTERACTIVE) {
    if (interactiveSchema.safeParse(originalTemplate).success) {
      const interactive = getInteractiveTemplateWithButtons(originalTemplate);
      return {
        template: interactive.template,
        templateButtons: interactive.buttons,
        isProcessed: true,
      };
    }
    return { template: getUnknownTemplate(''), isProcessed: true };
  }

  if (type === MessageType.CONTACTS) {
    if (contactTemplateSchema.safeParse(originalTemplate).success) {
      const contacts = getContactTemplate(originalTemplate);
      return {
        template: contacts.template,
        templateButtons: contacts.buttons,
        isProcessed: true,
      };
    }
  }

  if (originalTemplate && !isEmpty(originalTemplate)) {
    // Handle no-intent template
    if (noIntentTemplateSchema.safeParse(originalTemplate).success) {
      const currentTemplate = Object.entries(
        originalTemplate as NoIntentTemplate
      )?.[0];
      const { caption, filename, url } = currentTemplate?.[1] || {};
      const templateType =
        currentTemplate?.[0] === 'document' ? 'document' : 'image';
      return {
        template: getNoIntentTemplate(url, templateType, caption, filename),
        isProcessed: true,
      };
    }

    // Handle valid message template
    const template = getValidTemplate(
      originalTemplate as MsgTemplate,
      templateName
    );
    if (messageTemplateSchema.safeParse(template).success) {
      const curr = templateName ?? Object.keys(template)?.[0];
      const templateButtons = getButtonsFromTemplate(template) || [];
      const { cards = [], ...restTemplate } = template[curr];
      const carouselCards = cards.map((card) => {
        return {
          card,
          buttons: getCarouselCardButtons(card),
        };
      });
      return {
        template: { [curr]: restTemplate },
        templateButtons,
        currentTemplateName: curr,
        isProcessed: true,
        cards: carouselCards,
      };
    }
  }

  return { isProcessed: false };
};

export const useGetTemplateFromData = (
  envelope: MessageEnvelope
): RetriveTemplateResult => {
  const loggedInUserType = useGetLoggedInUserType();
  const workspace = getWorkspace(store.getState());
  const selectedPrincipal = useAppSelector(getSelectedPrincipalAccount);
  const { action, isLoading } = useAppSelector(getSelectedMessage) || {};
  const userPermissions = useAppSelector(getUserPermissions);

  // Extract all message properties
  const {
    originalTemplate,
    text,
    data,
    type,
    media,
    inviteId,
    fileUrl,
    formAnswerId,
    formId,
    channelId,
    actions,
  } = extractMessageProperties(envelope);

  // Get channel and related data
  const channel = getChannelById(channelId)(store.getState());
  const { channelName, customerId } = channel || {};
  const msgCustomerId = data?.inviteId || inviteId || customerId || '';

  // Get status maps and action data
  const userStatusMap = getMessageActionsData(actions, 'status');
  const orderStatusMap = getMessageActionsData(actions, 'orderStatus');
  const actionOrderId = Object.keys(actions?.['orderId'] || {})[0];
  const statusMap = { ...orderStatusMap, ...userStatusMap };

  // Create button props mapping
  const buttonPropsMap = createButtonPropsMap(
    data,
    msgCustomerId,
    envelope,
    actionOrderId,
    envelope?.message,
    formId,
    formAnswerId
  );

  // Determine seller name
  const sellerName = useMemo(() => {
    return loggedInUserType === 'distributor'
      ? selectedPrincipal?.spaceName || ''
      : workspace?.spaceName || '';
  }, [loggedInUserType, workspace, selectedPrincipal]);

  // Update button type map if needed
  let ButtonsTypeMap = { ...TemplateTypeButtonsMap };
  if (actionOrderId) {
    ButtonsTypeMap = updateTemplateTypeButtonsMap(ButtonsTypeMap, 'campaign', [
      'view_campaign',
      'view_order',
    ]) as TemplateButtonsMapType;
  }

  // Get buttons function
  const getButtons = (templateName: string) => {
    if (!(templateName in ButtonsTypeMap)) return [];

    const tmpName = templateName as TemplateKeys;
    const buttons: TemplateButtons[] = ButtonsTypeMap[tmpName]
      .map(
        (x: TemplateButtonKeys) =>
          TemplateButtonsMap(
            loggedInUserType === 'distributor',
            buttonPropsMap[x],
            action === x && isLoading,
            getCampaignStatus(tmpName, statusMap)?.text
          )[x]
      )
      .map((item) => ({ ...item, type: TemplateButtonTypeEnum.SYSTEM }));

    return buttons.filter(
      ({ permissions, condition }) =>
        condition &&
        (permissions.some((item) => userPermissions.includes(item)) ||
          permissions.length === 0)
    );
  };

  // Determine message type and template name
  const msgType = determineMessageType(formId, data, type);
  const tmpName =
    Object.keys(originalTemplate)?.length === 1
      ? Object.keys(originalTemplate)?.[0]
      : originalTemplate?.name || undefined;
  const templateName = msgType ? getTemplateKey(msgType, tmpName) : undefined;

  // Process original template
  const templateResult = processTemplate(
    originalTemplate,
    templateName,
    type,
    text
  );

  if (templateResult.isProcessed) {
    const systemButtons = templateName ? getButtons(templateName) : [];

    if (templateResult.templateButtons) {
      return {
        template: templateResult.template as MsgTemplate,
        buttons: templateResult.templateButtons,
        systemButtons: getButtons(
          templateResult.currentTemplateName || templateName || ''
        ),
        cards: templateResult.cards || [],
      };
    }

    return {
      template: templateResult.template as MsgTemplate,
      buttons: [],
      systemButtons,
      cards: templateResult.cards || [],
    };
  }

  // Handle data-based templates
  const hasMsgData = !isUndefined(data) && !isNull(data);
  if (hasMsgData) {
    const dataResult = handleDataBasedTemplates(
      data,
      templateName,
      sellerName,
      channelName,
      media,
      text,
      fileUrl
    );

    if (dataResult) {
      return {
        template: dataResult.template,
        buttons: [],
        systemButtons: templateName ? getButtons(templateName) : [],
        cards: [],
      };
    }
  }

  // Handle media fallback
  if (media.length > 0) {
    return {
      template: getMediaTemplateFromObject(media, text, fileUrl),
      buttons: [],
      systemButtons: templateName ? getButtons(templateName) : [],
      cards: [],
    };
  }

  // Final fallback
  return {
    template: getUnknownTemplate(text),
    buttons: [],
    systemButtons: templateName ? getButtons(templateName) : [],
    cards: [],
  };
};
